import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Target, Dumbbell, Scale, Calendar, ChevronRight, Gift, Star, Zap, Apple, Users, Activity, Clock, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Contact from '../components/Contact';

const prizes = [
  {
    place: '1st Place',
    rewards: [
      '1 Year Premium Membership (worth over $2000)',
      '$500 Cash Prize'
    ],
    color: 'from-blue-500/20 to-cyan-500/20',
    icon: Trophy
  },
  {
    place: '2nd Place',
    rewards: ['$300 Cash Prize'],
    color: 'from-blue-400/20 to-cyan-400/20',
    icon: Trophy
  },
  {
    place: '3rd Place',
    rewards: ['$200 Cash Prize'],
    color: 'from-blue-300/20 to-cyan-300/20',
    icon: Trophy
  }
];

const pointSystem = [
  {
    category: 'Male',
    rules: [
      'Fat mass decrease: 10 points per kg',
      'Muscle mass increase: 15 points per kg'
    ]
  },
  {
    category: 'Female',
    rules: [
      'Fat mass decrease: 10 points per kg',
      'Muscle mass increase: 20 points per kg'
    ]
  }
];

const features = [
  {
    icon: Target,
    title: 'Set Your Goal',
    description: 'Choose and achieve your personal fitness goal'
  },
  {
    icon: Dumbbell,
    title: 'Gain Muscle',
    description: 'Build lean muscle mass with expert guidance'
  },
  {
    icon: Scale,
    title: 'Lose Fat',
    description: 'Reduce body fat through proven methods'
  }
];

const services = [
  {
    icon: Dumbbell,
    title: 'Training Program',
    description: 'Personalized training program delivered via our app',
    benefits: [
      'Custom workout plans',
      'Progress tracking',
      'Exercise demonstrations',
      'Adjustable difficulty'
    ]
  },
  {
    icon: Apple,
    title: 'Nutrition Advice',
    description: 'Expert guidance on nutrition from qualified trainers',
    benefits: [
      'Personalized meal guidance',
      'Macro tracking support',
      'Supplement advice',
      'Nutrition education'
    ]
  },
  {
    icon: Users,
    title: 'Group Fitness',
    description: 'Unlimited access to all group fitness classes',
    benefits: [
      'All classes included',
      'Expert instruction',
      'Motivating environment',
      'Varied workout styles'
    ]
  },
  {
    icon: Activity,
    title: 'Body Scans',
    description: '4x Tanita body composition scans to track progress',
    benefits: [
      'Track body composition',
      'Monitor muscle growth',
      'Measure fat loss',
      'Regular progress checks'
    ]
  }
];

const goals = [
  'Weight loss and body transformation',
  'Muscle gain and strength improvement',
  'Enhanced fitness and endurance',
  'Better nutrition habits',
  'Increased energy levels',
  'Improved body composition'
];

export default function ChallengePage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[url('https://i.ibb.co/k2XPcbyX/20250426-Fitness-Portal-Hyrox-34.jpg')] bg-cover bg-center opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/50 to-black" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                className="inline-block mb-6"
              >
                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-1 rounded-full shadow-lg shadow-blue-500/20">
                  <div className="bg-black px-6 py-2 rounded-full flex items-center space-x-2">
                    <Trophy className="w-5 h-5 text-blue-400" />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 font-bold">
                      $6500 IN PRIZES TO BE WON
                    </span>
                    <Trophy className="w-5 h-5 text-blue-400" />
                  </div>
                </div>
              </motion.div>

              <h1 className="text-7xl font-extrabold mb-6">
                8 WEEK
                <div className="relative inline-block mx-4">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                    CHALLENGE
                  </span>
                </div>
              </h1>
              <p className="text-xl text-gray-400 mb-8">Transform your body and achieve your fitness goals</p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
                <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm rounded-full px-6 py-3">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 font-bold">
                      Starts 16th of June
                    </span>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm rounded-full px-6 py-3">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-5 h-5 text-blue-400" />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 font-bold">
                      Sign ups close 9th of June
                    </span>
                  </div>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="mt-8"
              >
                <a
                  href="https://widgets.mywellness.com/shop/fitnessportalakl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-4 rounded-full font-bold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 group"
                >
                  <span>JOIN THE CHALLENGE</span>
                  <ChevronRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Goals Section */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-bold mb-4">ACHIEVE YOUR GOALS</h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Have you always wanted to achieve a specific fitness goal but haven't quite managed yet? 
                This is your opportunity to make it happen with expert guidance and support.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {goals.map((goal, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-xl p-6 flex items-center space-x-4"
                >
                  <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0" />
                  <span className="text-lg">{goal}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-bold mb-4">WHAT'S INCLUDED</h2>
              <p className="text-xl text-gray-400">Everything you need to succeed in the challenge</p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  className="relative group"
                >
                  <motion.div
                    animate={{
                      opacity: [0.3, 0.6, 0.3],
                      scale: [1, 1.02, 1]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl blur-xl"
                  />
                  <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 h-full">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-4 rounded-xl">
                        <service.icon className="w-8 h-8 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold">{service.title}</h3>
                        <p className="text-gray-400">{service.description}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {service.benefits.map((benefit, i) => (
                        <div
                          key={i}
                          className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl p-4 flex items-center space-x-3"
                        >
                          <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                          <span className="text-gray-300">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Point System Section */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-bold mb-4">HOW TO WIN</h2>
              <p className="text-xl text-gray-400">Earn points and compete for amazing prizes</p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* How to Win */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="lg:col-span-1"
              >
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10 h-full">
                  <h3 className="text-2xl font-bold mb-6">Three Ways to Win</h3>
                  <div className="space-y-6">
                    {features.map((feature, index) => (
                      <div key={index} className="flex items-start space-x-4">
                        <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-3 rounded-xl">
                          <feature.icon className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                          <h4 className="font-bold mb-2">{feature.title}</h4>
                          <p className="text-gray-400">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Point System */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="lg:col-span-2"
              >
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10 h-full">
                  <h3 className="text-2xl font-bold mb-6">Point System</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {pointSystem.map((category, index) => (
                      <div key={index} className="space-y-4">
                        <h4 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                          {category.category}
                        </h4>
                        <ul className="space-y-4">
                          {category.rules.map((rule, i) => (
                            <li key={i} className="flex items-center space-x-3">
                              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                              <span className="text-gray-300">{rule}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Prizes Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="inline-block mb-6">
                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-1 rounded-full shadow-lg shadow-blue-500/20">
                  <div className="bg-black px-6 py-2 rounded-full flex items-center space-x-2">
                    <Trophy className="w-5 h-5 text-blue-400" />
                    <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                      WIN BIG
                    </span>
                    <Trophy className="w-5 h-5 text-blue-400" />
                  </div>
                </div>
              </div>
              <h2 className="text-6xl font-extrabold mb-4">PRIZES</h2>
              <p className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Over $6500 in prizes to be won
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {prizes.map((prize, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
                  className="relative group"
                >
                  <motion.div
                    animate={{
                      opacity: [0.3, 0.6, 0.3],
                      scale: [1, 1.02, 1]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className={`absolute inset-0 bg-gradient-to-br ${prize.color} rounded-2xl blur-xl`}
                  />
                  <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 transform hover:scale-105 transition-all duration-300">
                    <prize.icon className="w-16 h-16 text-blue-400 mb-6" />
                    <h3 className="text-3xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                      {prize.place}
                    </h3>
                    <ul className="space-y-4">
                      {prize.rewards.map((reward, i) => (
                        <li key={i} className="flex items-center space-x-3">
                          <Star className="w-5 h-5 text-blue-400 flex-shrink-0" />
                          <span className="text-xl font-semibold text-white">{reward}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-center mt-16"
            >
              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10 mb-8 inline-block">
                <p className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                  Plus spot prizes up for grabs throughout the challenge!
                </p>
              </div>
              <div>
                <a
                  href="https://widgets.mywellness.com/shop/fitnessportalakl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-4 rounded-full font-bold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 transform hover:scale-105"
                >
                  <span>SECURE YOUR SPOT NOW</span>
                  <ChevronRight className="w-5 h-5" />
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        <Contact />
      </main>

      <Footer />
    </div>
  );
}